// sales-page/src/pages/Checkout.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import salesLifetimeCss from './SalesLifetime.css?raw';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Page-scoped CSS: same design system as the main sales page, injected on
  // mount and removed on unmount so it never leaks onto other routes.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-checkout', 'true');
    styleEl.textContent = salesLifetimeCss;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  // Paddle is the active processor while Stripe's account is under review and
  // can't take live charges. Loaded once here rather than in index.html so it
  // never blocks pages that don't need it.
  useEffect(() => {
    if (window.Paddle) return;
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      window.Paddle?.Initialize({ token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN });
    };
    document.head.appendChild(script);
  }, []);

  // TrafikTrak Lifetime ($58 one-time) - the only plan sold on this page.
  const PADDLE_LIFETIME_PRICE_ID = 'pri_01m1rntf8p2rgrpsbtjaxn43dp';

  const { plan, email, promoterCode, visitorFingerprint } = location.state || {};

  const computedTotal = plan ? plan.price : 0;

  // Account setup fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation
  const passwordMinLength = password.length >= 8;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const passwordStrength = (() => {
    if (password.length === 0) return null;
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 1) return { label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600' };
    if (score <= 3) return { label: 'Fair', color: 'bg-yellow-500', textColor: 'text-yellow-600' };
    return { label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600' };
  })();

  const formValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    passwordMinLength &&
    passwordsMatch;

  useEffect(() => {
    // promoterCode is intentionally not required here -- the public lifetime
    // sales page has no affiliate gate, so a legitimate direct buyer arrives
    // with promoterCode === ''.
    if (!plan || !email) {
      navigate('/');
    }
  }, [plan, email, navigate]);

  const handleCheckout = async () => {
    if (!formValid) return;
    if (!window.Paddle) {
      setError('Checkout is still loading — please wait a moment and try again.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      // Hash the password client-side - the webhook creates the account with
      // this hash directly, the same way the Stripe checkout metadata did.
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      window.Paddle.Checkout.open({
        items: [{ priceId: PADDLE_LIFETIME_PRICE_ID, quantity: 1 }],
        customer: { email },
        customData: {
          customer_email: email,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          password_hash: passwordHash,
          promoter_code: promoterCode || '',
          visitor_fingerprint: visitorFingerprint || '',
        },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          successUrl: 'https://www.trafiktrak.com/signin?welcome=1',
        },
      });
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to open checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!plan) {
    return null;
  }

  return (
    <div className="min-h-screen font-['Arial',_sans-serif]">

      {/* Top bar */}
      <div style={{ background: '#111111', padding: '12px 16px', textAlign: 'center' }}>
        <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 700, letterSpacing: '0.02em' }}>
          &#128274; Secure checkout powered by Paddle &mdash; One-time payment, lifetime access
        </span>
      </div>

      <div className="sales-page-container" style={{ padding: '40px 16px 64px' }}>

        {/* Page header */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <h1 className="sales-section-heading" style={{ margin: '0 0 10px' }}>
            Complete Your Purchase
          </h1>
          <p style={{ fontSize: '17px', color: '#434343' }}>
            You are one step away from lifetime access to TrafikTrak.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">

          {/* ── LEFT COLUMN — Form (3/5) ── */}
          <div className="lg:col-span-3 space-y-6">

            {/* Error */}
            {error && (
              <div style={{ border: '1.5px solid #DC2626', background: '#FEF2F2', borderRadius: '4px', padding: '12px 16px' }}>
                <p style={{ fontSize: '14px', color: '#B91C1C', margin: 0, fontWeight: 700 }}>{error}</p>
              </div>
            )}

            {/* Section 1 — Verified email */}
            <div className="doc-boxed-section">
              <h2 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <span style={{ width: '26px', height: '26px', border: '1.5px solid #111111', color: '#111111', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, marginRight: '10px', flexShrink: 0 }}>1</span>
                Your Email
              </h2>
              <div className="checklist-item" style={{ background: '#F0FDF4', border: '1.5px solid #16A34A', borderRadius: '4px', padding: '12px 14px', marginBottom: 0 }}>
                <span className="check-icon">&#9989;</span>
                <div>
                  <p style={{ fontSize: '12px', color: '#166534', fontWeight: 700, margin: '0 0 2px' }}>Verified</p>
                  <p style={{ fontWeight: 700, color: '#111111', margin: 0, fontSize: '16px' }}>{email}</p>
                </div>
              </div>
            </div>

            {/* Section 2 — Account setup */}
            <div className="doc-boxed-section">
              <h2 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <span style={{ width: '26px', height: '26px', border: '1.5px solid #111111', color: '#111111', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, marginRight: '10px', flexShrink: 0 }}>2</span>
                Create Your Account
              </h2>

              <div className="space-y-5">
                {/* First Name + Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#111111', marginBottom: '8px' }}>
                      First Name <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #111111', borderRadius: '4px', fontFamily: 'inherit', fontSize: '16px', color: '#111111', background: '#FFFFFF' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#111111', marginBottom: '8px' }}>
                      Last Name <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Smith"
                      style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #111111', borderRadius: '4px', fontFamily: 'inherit', fontSize: '16px', color: '#111111', background: '#FFFFFF' }}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#111111', marginBottom: '8px' }}>
                    Password <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      style={{ width: '100%', padding: '12px 44px 12px 14px', border: '1.5px solid #111111', borderRadius: '4px', fontFamily: 'inherit', fontSize: '16px', color: '#111111', background: '#FFFFFF' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Strength indicator */}
                  {password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${passwordStrength?.color}`}
                            style={{
                              width: passwordStrength?.label === 'Weak' ? '33%' : passwordStrength?.label === 'Fair' ? '66%' : '100%'
                            }}
                          />
                        </div>
                        <span className={`text-xs font-semibold ${passwordStrength?.textColor}`}>
                          {passwordStrength?.label}
                        </span>
                      </div>
                      {!passwordMinLength && (
                        <p className="text-xs text-red-500 mt-1">Must be at least 8 characters</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#111111', marginBottom: '8px' }}>
                    Confirm Password <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      style={{
                        width: '100%', padding: '12px 44px 12px 14px', borderRadius: '4px', fontFamily: 'inherit', fontSize: '16px', color: '#111111', background: '#FFFFFF',
                        border: confirmPassword.length > 0 ? (passwordsMatch ? '1.5px solid #16A34A' : '1.5px solid #DC2626') : '1.5px solid #111111'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                  {confirmPassword.length > 0 && passwordsMatch && (
                    <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3 — Features included */}
            <div className="doc-boxed-section">
              <h2 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <span style={{ width: '26px', height: '26px', border: '1.5px solid #111111', color: '#111111', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, marginRight: '10px', flexShrink: 0 }}>3</span>
                What You Are Getting
              </h2>
              <div>
                {plan.features.map((feature, index) => (
                  <div key={index} className="checklist-item" style={{ fontSize: '16px' }}>
                    <span className="check-icon">&#9989;</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* ── RIGHT COLUMN — Order summary + CTA (2/5) ── */}
          <div className="lg:col-span-2">
            <div className="sticky top-8 space-y-4">

              {/* Order summary card */}
              <div className="doc-boxed-section">
                <h2 style={{ fontSize: '19px', fontWeight: 900, color: '#111111', marginBottom: '16px' }}>Order Summary</h2>

                <div style={{ marginBottom: '18px' }}>
                  <div className="flex justify-between items-start" style={{ marginBottom: '8px' }}>
                    <div>
                      <p style={{ fontWeight: 800, color: '#111111', margin: 0 }}>{plan.name}</p>
                      <p style={{ fontSize: '14px', color: '#666666', margin: '2px 0 0' }}>{plan.description}</p>
                    </div>
                    <p style={{ fontWeight: 800, color: '#111111', margin: 0, marginLeft: '12px' }}>${plan.price}</p>
                  </div>
                  <div style={{ display: 'inline-block', border: '1.5px solid #16A34A', color: '#166534', background: '#F0FDF4', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 800 }}>
                    &#9989; LIFETIME ACCESS
                  </div>
                </div>

                <div style={{ borderTop: '1.5px solid #D1D5DB', paddingTop: '16px', marginBottom: '20px' }}>
                  <div className="flex justify-between items-center">
                    <span style={{ fontSize: '14px', color: '#434343' }}>One-time payment</span>
                    <span style={{ fontSize: '30px', fontWeight: 900, color: '#1D4ED8' }}>${computedTotal}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#999999', margin: '2px 0 0', textAlign: 'right' }}>No recurring fees. Ever.</p>
                </div>

                {/* Incomplete form warning */}
                {!formValid && (firstName.length > 0 || lastName.length > 0 || password.length > 0 || confirmPassword.length > 0) && (
                  <div style={{ marginBottom: '14px', border: '1.5px solid #D97706', background: '#FFFBEB', borderRadius: '4px', padding: '10px 12px' }}>
                    <p style={{ fontSize: '12px', color: '#92400E', fontWeight: 700, margin: 0 }}>
                      Please complete all account fields before proceeding.
                    </p>
                  </div>
                )}

                {/* CTA button */}
                <button
                  onClick={handleCheckout}
                  disabled={loading || !formValid}
                  className="doc-cta-button"
                  style={{ opacity: (loading || !formValid) ? 0.55 : 1, cursor: (loading || !formValid) ? 'not-allowed' : 'pointer' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    `🔒 Pay $${computedTotal} — Complete Purchase`
                  )}
                </button>

                {/* Trust badges */}
                <div style={{ marginTop: '14px' }}>
                  <div className="checklist-item" style={{ fontSize: '13px', color: '#434343', marginBottom: '6px' }}>
                    <span className="check-icon" style={{ fontSize: '14px' }}>&#9989;</span>
                    Secure Paddle checkout
                  </div>
                  <div className="checklist-item" style={{ fontSize: '13px', color: '#434343', marginBottom: 0 }}>
                    <span className="check-icon" style={{ fontSize: '14px' }}>&#9989;</span>
                    SSL encrypted &middot; All major cards accepted
                  </div>
                </div>
              </div>

              {/* Guarantee card */}
              <div className="doc-boxed-section" style={{ background: '#FFF9E6', borderColor: '#D97706' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#111111', marginBottom: '6px' }}>&#128737;&#65039; 14-Day Money-Back Guarantee</h3>
                <p style={{ fontSize: '13px', color: '#434343', lineHeight: 1.5, margin: 0 }}>
                  Try TrafikTrak risk-free for 14 days. Not satisfied? Email us for a full refund. No questions asked.
                </p>
              </div>

              {/* Back link */}
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => navigate('/')}
                  style={{ background: 'none', border: 'none', color: '#666666', textDecoration: 'underline', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  &larr; Back to sales page
                </button>
              </div>

            </div>
          </div>

        </div>

        {/* Bottom trust bar */}
        <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid #D1D5DB', textAlign: 'center' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '20px', fontSize: '13px', color: '#6B7280', marginBottom: '10px' }}>
            <span>&#128737;&#65039; 14-Day Money-Back Guarantee</span>
            <span>&#128274; Secure Payment Processing</span>
            <span>&#128179; All Major Cards Accepted</span>
          </div>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
            Powered by Paddle &middot; Your payment information is encrypted and secure
          </p>
        </div>

      </div>
    </div>
  );
};

export default Checkout;